import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState
} from 'react'

import type { AppBrand } from '../constants'
import { getAppBrand } from '../utils/getAppBrand'
import { getBrandName } from '../utils/getBrandName'

export interface AppBrandContextValue {
  appBrand: AppBrand
  brandName: string
  resetAppBrand: () => void
  setAppBrand: (appBrand: AppBrand) => void
}

export const AppBrandContext = createContext<AppBrandContextValue | undefined>(
  undefined
)

interface AppBrandProviderProps extends PropsWithChildren {
  initialAppBrand?: AppBrand
}

export const AppBrandProvider = ({
  children,
  initialAppBrand
}: AppBrandProviderProps) => {
  const getInitialAppBrand = useCallback(
    () => initialAppBrand ?? getAppBrand(),
    [initialAppBrand]
  )

  const [appBrand, setAppBrand] = useState<AppBrand>(getInitialAppBrand)

  const resetAppBrand = useCallback(() => {
    setAppBrand(getInitialAppBrand())
  }, [getInitialAppBrand])

  const value = useMemo(
    () => ({
      appBrand,
      brandName: getBrandName(appBrand),
      resetAppBrand,
      setAppBrand
    }),
    [appBrand, resetAppBrand]
  )

  return (
    <AppBrandContext.Provider value={value}>
      {children}
    </AppBrandContext.Provider>
  )
}
